import { create } from "zustand"
import { persist } from "zustand/middleware"

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3021/api"

interface User {
	id: string
	email: string
	name: string
	role?: string
	company_id?: string
}

interface Company {
	id: string
	name: string
	logo_url?: string
	is_platform_admin?: boolean
}

interface AuthState {
	user: User | null
	token: string | null
	company: Company | null
	isAuthenticated: boolean
	isLoading: boolean
	isSessionVerified: boolean
	login: (email: string, password: string) => Promise<boolean>
	logout: () => void
	verifySession: () => Promise<boolean>
	fetchCompanyInfo: () => Promise<void>
	updateUser: (user: User, token?: string) => void
	setCompany: (company: Company | null) => void
}

export const useAuthStore = create<AuthState>()(
	persist(
		(set, get) => ({
			user: null,
			token: null,
			company: null,
			isAuthenticated: false,
			isLoading: false,
			isSessionVerified: false,

			login: async (email, password) => {
				set({ isLoading: true })
				try {
					const res = await fetch(`${API_BASE_URL}/auth/login`, {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ email, password }),
					})
					const data = await res.json()
					if (data.success && data.user) {
						set({ user: data.user, token: data.token, isAuthenticated: true, isLoading: false, isSessionVerified: true })
						if (data.user.company_id) await get().fetchCompanyInfo()
						return true
					}
					set({ isLoading: false })
					return false
				} catch {
					set({ isLoading: false })
					return false
				}
			},

			logout: () => {
				set({ user: null, token: null, company: null, isAuthenticated: false, isSessionVerified: true })
			},

			verifySession: async () => {
				const { token } = get()
				if (!token) {
					set({ isAuthenticated: false, isSessionVerified: true })
					return false
				}
				try {
					const res = await fetch(`${API_BASE_URL}/auth/me`, {
						headers: { Authorization: `Bearer ${token}` },
					})
					if (res.ok) {
						const data = await res.json()
						set({ user: data.user, isAuthenticated: true, isSessionVerified: true })
						if (data.user?.company_id) await get().fetchCompanyInfo()
						return true
					}
					set({ user: null, token: null, isAuthenticated: false, isSessionVerified: true })
					return false
				} catch {
					set({ user: null, token: null, isAuthenticated: false, isSessionVerified: true })
					return false
				}
			},

			updateUser: (user, token) => {
				set({ user, ...(token ? { token } : {}) })
			},

			setCompany: (company) => {
				set({ company })
			},

			fetchCompanyInfo: async () => {
				const { token, user } = get()
				if (!token || !user?.company_id) return
				try {
					const res = await fetch(`${API_BASE_URL}/companies/me/info`, {
						headers: { Authorization: `Bearer ${token}` },
					})
					if (res.ok) {
						const data = await res.json()
						if (data.success && data.data) set({ company: data.data })
					}
				} catch {
					// ignore
				}
			},
		}),
		{
			name: "auth-storage-licitacions",
			partialize: (state) => ({
				user: state.user,
				token: state.token,
				company: state.company,
				isAuthenticated: state.isAuthenticated,
			}),
		}
	)
)
